import React, { useState, useEffect } from 'react';
import { UserProfile, RankRequirement } from '../types';
import { db, handleFirestoreError, OperationType, auth } from '../lib/firebase';
import { updateDoc, doc, collection, setDoc } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ROLES } from '../constants';
import { isSystemAdmin, isUserManager as checkUserManager } from '../lib/admin';
import { toast } from 'sonner';
import { validatePassword } from '../lib/utils';

interface MemberFormProps {
  isOpen: boolean;
  onClose: () => void;
  member: UserProfile | null;
  currentUserProfile: UserProfile | null;
  ranks: RankRequirement[];
  departments: { id: string, name: string }[];
}



export default function MemberForm({ isOpen, onClose, member, currentUserProfile, ranks, departments }: MemberFormProps) {
  const [formData, setFormData] = useState<Partial<UserProfile>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');

  const isUserManager = checkUserManager(currentUserProfile);

  const isAdmin = isSystemAdmin(currentUserProfile);

  useEffect(() => {
    setPasswordInput('');
    if (member) {
      const data = { ...member };
      if (data.currentRank === 'CDR') {
        data.currentRank = 'CMDR';
      }
      setFormData(data);
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        roles: ['member'],
        status: 'active',
        ots: false,
        occ: false,
        sdfpp: false,
        manualPoints: 0,
        zavalaJoinDate: new Date().toISOString().split('T')[0],
        promotionRankDate: new Date().toISOString().split('T')[0],
        currentRank: 'REC',
        departments: []
      });
    }
  }, [member, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if password needs to be verified
    if (passwordInput) {
      if (isSelfEdit) {
        const errorMsg = validatePassword(passwordInput);
        if (errorMsg) {
          toast.error(errorMsg);
          return;
        }
      }
    } else if (!member) {
      // In create mode, password is required
      toast.error('Password is required for new crew member registration.');
      return;
    }

    setIsSubmitting(true);
    try {
      const dbData = Object.fromEntries(
        Object.entries(formData).filter(([k, v]) => v !== undefined && k !== 'id')
      );
      
      if (passwordInput) {
        dbData.password = passwordInput;
        dbData.passwordLastChanged = new Date().toISOString();
      }

      if (member?.uid) {
        await updateDoc(doc(db, 'users', member.uid), dbData);
        toast.success('Personnel record updated');
      } else {
        const newUserRef = doc(collection(db, 'users'));
        const newUid = newUserRef.id;
        dbData.uid = newUid;
        await setDoc(newUserRef, dbData);
        toast.success('New crew member registered successfully');
      }
      onClose();
      setPasswordInput('');
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'users');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAbort = () => {
    setIsAborting(true);
    setTimeout(() => {
      onClose();
      setIsAborting(false);
    }, 400);
  };

  const isSelfEdit = currentUserProfile?.uid === member?.uid;
  const canEditSensitive = isAdmin; // Only admins can edit rank/points/role/dates normally
  // However, the user said "just like the admin can", so I will allow self-edit of most fields
  // BUT I will still block role change for non-admins to be safe and match rules.
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card-bg border border-border-color text-[var(--foreground)] w-[100dvw] max-w-none md:max-w-5xl h-[100dvh] md:h-auto md:max-h-[90vh] overflow-y-auto shadow-2xl p-0 rounded-none md:rounded-lg">
        <DialogHeader className="px-6 lg:px-12 py-6 lg:py-8 border-b border-border-color bg-white/5">
          <DialogTitle className="uppercase text-accent-gold tracking-widest font-bold text-2xl lg:text-3xl">
            {member ? `Personnel Record: ${member.firstName} ${member.lastName}` : 'Personnel Record: REGISTER NEW CREW MEMBER'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="px-6 lg:px-12 py-8 lg:py-12 space-y-8 lg:space-y-10 max-w-7xl mx-auto">
          <div className="space-y-6">
            {/* Row 1: SCC# and Ship Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-2">
                <Label htmlFor="sccNumber" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">SCC#</Label>
                <Input 
                  id="sccNumber" 
                  value={formData.sccNumber || ''} 
                  onChange={e => setFormData({ ...formData, sccNumber: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isUserManager && !isSelfEdit}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shipName" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Ship Name</Label>
                <Input 
                  id="shipName" 
                  value={formData.shipName || ''} 
                  onChange={e => setFormData({ ...formData, shipName: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isUserManager && !isSelfEdit}
                />
              </div>
            </div>

            {/* Row 2: First and Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">First Name</Label>
                <Input 
                  id="firstName" 
                  value={formData.firstName || ''} 
                  onChange={e => setFormData({ ...formData, firstName: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  required
                  disabled={!isUserManager && !isSelfEdit}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Last Name</Label>
                <Input 
                  id="lastName" 
                  value={formData.lastName || ''} 
                  onChange={e => setFormData({ ...formData, lastName: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  required
                  disabled={!isUserManager && !isSelfEdit}
                />
              </div>
            </div>

            {/* Row 3: Login Email and SFI Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Login Email Address</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={formData.email || ''} 
                  onChange={e => setFormData({ ...formData, email: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  required
                  disabled={!isAdmin && !!member}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sfiEmail" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">SFI Email Address</Label>
                <Input 
                  id="sfiEmail" 
                  type="email"
                  value={formData.sfiEmail || ''} 
                  onChange={e => setFormData({ ...formData, sfiEmail: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isUserManager && !isSelfEdit}
                />
              </div>
            </div>

            {/* Row 4: Cell Phone and Date of Birth */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-2">
                <Label htmlFor="cell" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Cell Frequency (Phone)</Label>
                <Input 
                  id="cell" 
                  value={formData.cell || ''} 
                  onChange={e => setFormData({ ...formData, cell: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isUserManager && !isSelfEdit}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dob" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Date of Birth</Label>
                <Input 
                  id="dob" 
                  type="date"
                  value={formData.dob || ''} 
                  onChange={e => setFormData({ ...formData, dob: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isUserManager && !isSelfEdit}
                />
              </div>
            </div>

            {/* Row 5: SFI Join and SFI Expiration Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-2">
                <Label htmlFor="sfiJoinDate" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">SFI Join Date</Label>
                <Input 
                  id="sfiJoinDate" 
                  type="date"
                  value={formData.sfiJoinDate || ''} 
                  onChange={e => setFormData({ ...formData, sfiJoinDate: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isAdmin && !isSelfEdit}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sfiExpireDate" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">SFI Expiration Date</Label>
                <Input 
                  id="sfiExpireDate" 
                  type="date"
                  value={formData.sfiExpireDate || ''} 
                  onChange={e => setFormData({ ...formData, sfiExpireDate: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isAdmin && !isSelfEdit}
                />
              </div>
            </div>

            {/* Row 6: Zavala Join and Zavala Expiration Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-2">
                <Label htmlFor="zavalaJoinDate" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Zavala Join Date</Label>
                <Input 
                  id="zavalaJoinDate" 
                  type="date"
                  value={formData.zavalaJoinDate || ''} 
                  onChange={e => setFormData({ ...formData, zavalaJoinDate: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  required
                  disabled={!isAdmin && !isSelfEdit}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zavalaExpireDate" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Zavala Expiration Date</Label>
                <Input 
                  id="zavalaExpireDate" 
                  type="date"
                  value={formData.zavalaExpireDate || ''} 
                  onChange={e => setFormData({ ...formData, zavalaExpireDate: e.target.value })} 
                  className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                  disabled={!isAdmin && !isSelfEdit}
                />
              </div>
            </div>

            {/* Row 7: Last Promotion Rank Date */}
            <div className="space-y-2">
              <Label htmlFor="promotionRankDate" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Last Promotion Rank Date</Label>
              <Input 
                id="promotionRankDate" 
                type="date"
                value={formData.promotionRankDate || ''} 
                onChange={e => setFormData({ ...formData, promotionRankDate: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                required
                disabled={!isAdmin}
              />
            </div>

            {/* Row 8: Current Rank and Department */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-2">
                <Label htmlFor="currentRank" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Current Rank</Label>
                <Select 
                  value={formData.currentRank} 
                  onValueChange={val => setFormData({ ...formData, currentRank: val })}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg">
                    <SelectValue placeholder="Select rank" />
                  </SelectTrigger>
                  <SelectContent className="bg-card-bg border-border-color text-[var(--foreground)]">
                    {ranks.map(r => (
                      <SelectItem key={r.id} value={r.id} className="focus:bg-accent-blue focus:text-[var(--foreground)]">{r.name} ({r.shortName})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest block mb-2">Departments</Label>
                <div className="grid grid-cols-2 gap-2 bg-[#0d1117] p-3 rounded-lg border-2 border-border-color max-h-[160px] overflow-y-auto">
                  {departments.map(dept => {
                    // Fallback to reading string `department` if `departments` array is empty but string exists
                    const currentDepts = formData.departments || (formData.department ? [formData.department] : []);
                    const isChecked = currentDepts.includes(dept.name);
                    return (
                      <label key={dept.id} className="flex items-center space-x-2 text-sm cursor-pointer group">
                        <Checkbox 
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            if (!isAdmin) return;
                            let newDepts = [...currentDepts];
                            if (checked) {
                              newDepts.push(dept.name);
                            } else {
                              newDepts = newDepts.filter(d => d !== dept.name);
                            }
                            setFormData({ ...formData, departments: newDepts, department: newDepts[0] || '' });
                          }}
                          disabled={!isAdmin}
                        />
                        <span className="text-[var(--foreground)]/80 group-hover:text-[var(--foreground)] transition-colors">{dept.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Row 9: Manual Points Adjustment */}
            <div className="space-y-2">
              <Label htmlFor="manualPoints" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Manual Points Adjustment</Label>
              <Input 
                id="manualPoints" 
                type="number"
                value={formData.manualPoints || 0} 
                onChange={e => setFormData({ ...formData, manualPoints: parseInt(e.target.value) || 0 })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg"
                disabled={!isAdmin}
              />
            </div>

            {/* Credentials / Password Section */}
            <div className="p-6 bg-white/5 rounded border border-border-color space-y-4">
              <h3 className="text-accent-gold uppercase tracking-wider font-bold text-sm">Credentials & Security</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                <div className="space-y-2">
                  <Label htmlFor="passwordInput" className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">
                    {member ? 'New Password (leave blank to keep current)' : 'Password'}
                  </Label>
                  <div className="flex gap-2">
                    <Input 
                      id="passwordInput" 
                      type="password"
                      value={passwordInput} 
                      onChange={e => setPasswordInput(e.target.value)} 
                      className="bg-[#0d1117] border-border-color focus:border-accent-blue h-12 text-lg flex-1"
                      required={!member}
                    />
                    {formData.email && (
                      <button
                        type="button"
                        onClick={() => {
                          const email = formData.email || '';
                          const name = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
                          const subject = encodeURIComponent('USS Zavala LCARS Access Credentials');
                          const body = encodeURIComponent(
                            `Attention Officer ${name},\n\n` +
                            `Your secure credentials for the USS Zavala LCARS Command Center have been configured.\n\n` +
                            `Access Console: https://www.usszavala.org/lcars/\n` +
                            `User Email: ${email}\n` +
                            `Passcode: ${passwordInput || formData.password || 'Z@v@l@'}\n\n` +
                            `Please log in to check your roster shift schedule. If you wish to change your passcode, you can do so in your Profile view.\n\n` +
                            `USS Zavala Communications Relay\n` +
                            `comms@usszavala.org`
                          );
                          window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
                        }}
                        className="h-12 px-4 bg-lcars-blue text-black hover:bg-lcars-blue/80 rounded-md text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap"
                        title="Draft email containing passcode credentials using local mail client"
                      >
                        ✉ Email credentials
                      </button>
                    )}
                  </div>
                </div>
                {member && (
                  <div className="space-y-2">
                    <Label className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Password Last Changed</Label>
                    <div className="h-12 flex items-center bg-[#0d1117]/50 border border-border-color/30 rounded-md px-3 text-[var(--foreground)]/70 text-sm">
                      {formData.passwordLastChanged ? new Date(formData.passwordLastChanged).toLocaleString() : 'Never'}
                    </div>
                  </div>
                )}
              </div>

              {member && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 pt-2">
                  <div className="space-y-2">
                    <Label className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Last Login Time</Label>
                    <div className="h-12 flex items-center bg-[#0d1117]/50 border border-border-color/30 rounded-md px-3 text-[var(--foreground)]/70 text-sm">
                      {formData.lastLogin ? new Date(formData.lastLogin).toLocaleString() : 'Never'}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">Last Login IP Address</Label>
                    <div className="h-12 flex items-center bg-[#0d1117]/50 border border-border-color/30 rounded-md px-3 text-[var(--foreground)]/70 text-sm">
                      {formData.lastLoginIp || 'Unknown'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Row 9: System Access Level (User Manager Only) */}
            {isUserManager && (
              <div className="space-y-4 p-6 bg-white/5 rounded border border-border-color">
                <Label className="text-[0.75rem] uppercase font-bold text-text-secondary tracking-widest">System Access Levels</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ROLES.map(role => (
                    <div key={role.id} className="flex items-center space-x-3">
                      <Checkbox 
                        id={`role-${role.id}`}
                        checked={formData.roles?.includes(role.id)}
                        onCheckedChange={(checked) => {
                          const currentRoles = formData.roles || [];
                          let newRoles: string[];
                          
                          if (checked) {
                            // If read_only or guest is selected, it should be the only role
                            if (role.id === 'read_only' || role.id === 'guest') {
                              newRoles = [role.id];
                            } else {
                              newRoles = [...currentRoles.filter(r => r !== 'read_only' && r !== 'guest'), role.id];
                            }
                          } else {
                            newRoles = currentRoles.filter(r => r !== role.id);
                          }
                          
                          // Ensure at least one role
                          if (newRoles.length === 0) newRoles = ['member'];
                          
                          setFormData({ ...formData, roles: newRoles });
                        }}
                        disabled={role.id === 'admin' && !isAdmin} // Only admins can grant admin role
                      />
                      <div className="grid gap-1.5 leading-none">
                        <Label 
                          htmlFor={`role-${role.id}`}
                          className="text-sm font-bold uppercase tracking-wider cursor-pointer"
                        >
                          {role.name}
                        </Label>
                        <p className="text-xs text-text-secondary">
                          {role.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 p-6 bg-white/5 rounded border border-border-color">
            <div className="flex items-center space-x-4">
              <Checkbox 
                id="statusActive" 
                checked={formData.status !== 'inactive'} 
                onCheckedChange={val => setFormData({ ...formData, status: val ? 'active' : 'inactive' })} 
                className="w-6 h-6 border-lcars-blue data-[state=checked]:bg-lcars-blue"
                disabled={!isAdmin}
              />
              <Label htmlFor="statusActive" className="text-[0.85rem] font-bold uppercase tracking-widest text-text-secondary cursor-pointer">
                ACTIVE PERSONNEL
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <Checkbox 
                id="ots" 
                checked={formData.ots} 
                onCheckedChange={val => setFormData({ ...formData, ots: !!val })} 
                className="w-6 h-6 border-accent-blue data-[state=checked]:bg-accent-blue"
                disabled={!isAdmin}
              />
              <Label htmlFor="ots" className="text-[0.85rem] font-bold uppercase tracking-widest text-text-secondary cursor-pointer">
                OTS Passed
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <Checkbox 
                id="occ" 
                checked={formData.occ} 
                onCheckedChange={val => setFormData({ ...formData, occ: !!val })} 
                className="w-6 h-6 border-accent-blue data-[state=checked]:bg-accent-blue"
                disabled={!isAdmin}
              />
              <Label htmlFor="occ" className="text-[0.85rem] font-bold uppercase tracking-widest text-text-secondary cursor-pointer">
                OCC Passed
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <Checkbox 
                id="sdfpp" 
                checked={formData.sdfpp} 
                onCheckedChange={val => setFormData({ ...formData, sdfpp: !!val })} 
                className="w-6 h-6 border-accent-blue data-[state=checked]:bg-accent-blue"
                disabled={!isAdmin}
              />
              <Label htmlFor="sdfpp" className="text-[0.85rem] font-bold uppercase tracking-widest text-text-secondary cursor-pointer">
                SFDPP Passed
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <Checkbox 
                id="duesPaid" 
                checked={formData.duesPaidYear === new Date().getFullYear()} 
                onCheckedChange={val => setFormData({ ...formData, duesPaidYear: val ? new Date().getFullYear() : 0 })} 
                className="w-6 h-6 border-accent-blue data-[state=checked]:bg-accent-blue"
                disabled={!isUserManager}
              />
              <Label htmlFor="duesPaid" className="text-[0.85rem] font-bold uppercase tracking-widest text-text-secondary cursor-pointer">
                Chapter Dues Paid ({new Date().getFullYear()})
              </Label>
            </div>
          </div>

          <DialogFooter className="pt-8 border-t border-border-color flex flex-col sm:flex-row gap-4">
            <button 
              type="button" 
              onClick={handleAbort}
              disabled={isSubmitting || isAborting}
              className={`lcars-pill bg-lcars-tan text-black px-8 py-3 text-[0.85rem] font-bold uppercase tracking-[0.2em] ${isAborting ? 'opacity-50' : ''}`}
            >
              {isAborting ? 'ABORTING...' : 'Aborted'}
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting || isAborting}
              className={`lcars-pill bg-lcars-blue text-black px-12 py-3 text-[0.85rem] font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                  PROCESSING...
                </>
              ) : (
                member ? 'Update Personnel Record' : 'Register New Crew Member'
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
